/**
 * BulkUploadDialog — upload a JSON file to add/update services in bulk.
 * Implements mockup SC7 + SC8 (error states).
 */
import { useState, useRef, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Upload, X, FileJson } from 'lucide-react'
import { toast } from 'sonner'

import { useUpsertServices } from '@/api/service-catalog'
import type { ServiceDetail } from '@/lib/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BulkUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

interface ParseResult {
  services: ServiceDetail[]
  error: string | null
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function parseAndValidate(text: string): ParseResult {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return { services: [], error: 'Could not parse file. Make sure it\'s valid JSON.' }
  }

  // Accept either { services: [...] } or a plain [...]
  let services: unknown[]
  if (Array.isArray(parsed)) {
    services = parsed
  } else if (
    parsed &&
    typeof parsed === 'object' &&
    'services' in parsed &&
    Array.isArray((parsed as { services: unknown[] }).services)
  ) {
    services = (parsed as { services: unknown[] }).services
  } else {
    return { services: [], error: 'Expected a JSON array or an object with a "services" array.' }
  }

  if (services.length === 0) {
    return { services: [], error: 'No services found in file.' }
  }

  // Validate names
  const emptyNameIndices: number[] = []
  const nameMap = new Map<string, number[]>()

  for (let i = 0; i < services.length; i++) {
    const svc = services[i] as { name?: string }
    const name = svc?.name?.trim()
    if (!name) {
      emptyNameIndices.push(i + 1) // 1-indexed for user
    } else {
      const existing = nameMap.get(name) ?? []
      existing.push(i + 1)
      nameMap.set(name, existing)
    }
  }

  const errors: string[] = []
  if (emptyNameIndices.length > 0) {
    errors.push(`${emptyNameIndices.length} service(s) have empty names (items ${emptyNameIndices.join(', ')}).`)
  }

  const duplicates = [...nameMap.entries()].filter(([, indices]) => indices.length > 1)
  for (const [name, indices] of duplicates) {
    errors.push(`Duplicate service name: ${name} (items ${indices.join(', ')}).`)
  }

  if (errors.length > 0) {
    return { services: [], error: errors.join(' ') }
  }

  return { services: services as ServiceDetail[], error: null }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BulkUploadDialog({
  open,
  onOpenChange,
  onSuccess,
}: BulkUploadDialogProps) {
  const upsertMutation = useUpsertServices()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [parsedServices, setParsedServices] = useState<ServiceDetail[]>([])
  const [parseError, setParseError] = useState<string | null>(null)

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setFileName(null)
      setParsedServices([])
      setParseError(null)
    }
  }, [open])

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (!file) return

      setFileName(file.name)

      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target?.result as string
        if (!text) {
          setParseError('Failed to read the file.')
          setParsedServices([])
          return
        }
        const result = parseAndValidate(text)
        setParsedServices(result.services)
        setParseError(result.error)
      }
      reader.onerror = () => {
        setParseError('Failed to read the file.')
        setParsedServices([])
      }
      reader.readAsText(file)
    },
    []
  )

  function handleRemoveFile() {
    setFileName(null)
    setParsedServices([])
    setParseError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  async function handleUpload() {
    if (parsedServices.length === 0) return

    try {
      await upsertMutation.mutateAsync(parsedServices)
      toast.success(`Uploaded ${parsedServices.length} services`)
      onSuccess?.()
      onOpenChange(false)
    } catch {
      toast.error('Failed to upload services')
    }
  }

  function handleCancel() {
    onOpenChange(false)
  }

  const previewServices = parsedServices.slice(0, 5)
  const remainingCount = parsedServices.length - previewServices.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Services</DialogTitle>
          <DialogDescription>
            Upload a JSON file to add or update services. Existing services with
            matching names will be updated. All other services remain untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* File drop zone */}
          <div
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              const file = e.dataTransfer.files[0]
              if (file) handleFileChange(file)
            }}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drop a .json file here or click to browse
            </p>
            <input
              ref={fileInputRef}
              data-testid="file-input"
              type="file"
              accept=".json"
              className="hidden"
              onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
            />
          </div>

          {/* File info + remove */}
          {fileName && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <FileJson className="h-4 w-4" />
                <span>{fileName}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                aria-label="Remove file"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Error display */}
          {parseError && (
            <p className="text-sm text-destructive">{parseError}</p>
          )}

          {/* Preview */}
          {parsedServices.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">
                Preview: {parsedServices.length} services found
              </p>
              <div className="border rounded-md p-3 space-y-1 text-sm">
                {previewServices.map((svc) => (
                  <div key={svc.name}>• {svc.name}</div>
                ))}
                {remainingCount > 0 && (
                  <div className="text-muted-foreground">
                    ... and {remainingCount} more
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Services with matching names will be updated (upsert). Services
                not in this file will NOT be removed.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={upsertMutation.isPending}>
            Cancel
          </Button>
          {parsedServices.length > 0 && (
            <Button
              onClick={handleUpload}
              disabled={upsertMutation.isPending || parsedServices.length === 0}
            >
              {upsertMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
